/**
 * Waits for OneTrust Banner to appear on the page.
 * If the element is present, the observer disconnects and the promise resolves immediately.
 * Otherwise it uses `MutationObserver` to monitor changes in the DOM and resolves once the element appears.
 *
 * @async
 * @function
 * @name waitForOneTrustBanner
 * @param {string}
 * @returns {Promise<void|any>}
 *
 *
 */

export type MinimalBannerReason = | "present-at-start" | "found by MO" | "timeout" | "bad-selector";
export type SmartBannerReason = | MinimalBannerReason | "cookie-present";

export const waitForOneTrustBanner = (rootSelector: string, options: { timeoutMs?: number } = {}): Promise<SmartBannerReason> => {
    const timeoutMs = options.timeoutMs ?? 5000;
    console.log('[banner] waiting for ' + rootSelector)

    return new Promise<SmartBannerReason>((resolve) => {
        let settled = false;
        let observer: MutationObserver | null = null;
        let timerId: number | null = null;

        const settle = (reason: SmartBannerReason) => {
            if(settled) return;
            settled = true;

            try { observer?.disconnect(); } catch {}
            observer = null;
    
            if(timerId !== null) {
                window.clearTimeout(timerId);
                timerId = null;
            }
    
            console.log(`[banner] resolved via  ${reason}`);
            resolve(reason);
        };

        // validate selector 
        if(!rootSelector || typeof rootSelector !== "string") {
            console.log("REASON: bad-selector")
            settle("bad-selector");
            return;
        }

        if(document.querySelector(rootSelector)){
            console.log("REASON: present-at-start");
            settle("present-at-start");
            return;
        }

        if(document.cookie.includes("OptanonConsent")) {
            console.log("REASON: cookie-present");
            settle("cookie-present");
            return;
        }

        observer = new MutationObserver(() => {
            if (document.querySelector(rootSelector)) {
                console.log("REASON: found by MO");
                settle("found by MO");
                return;
            }
            if(document.cookie.includes("OptanonConsent")) {
                console.log("REASON: cookie-present");
                settle("cookie-present");
                return;
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        timerId = window.setTimeout(() => settle("timeout"), timeoutMs);
    });
};
